import { create } from 'zustand';
import type { LineaRegistro, ProgresoConversion } from './api.js';

interface Progreso {
  progressPct: number;
  speedBps: number;
  sizeDone: number;
  sourcesTotal: number;
  sourcesXfer: number;
}

/** Lo que se ha recibido en vivo del registro de UNA descarga. */
interface RegistroVivo {
  /** De qué intento son estas líneas. Al empezar otro se tiran las de antes: mezclar dos
   *  intentos en la misma pantalla es peor que no tener registro. */
  runId: string;
  lineas: LineaRegistro[];
}

/** Tope de líneas que se guardan en vivo de un intento. El mismo que aplica la API al
 *  guardarlas: pasado ese punto, lo que falta se lee al recargar. */
const MAX_LINEAS_VIVAS = 2_000;

interface EstadoEventos {
  conectado: boolean;
  /** Progreso por hash MD4. Vive aparte de la caché de TanStack Query a propósito:
   *  cambia cada segundo, y meterlo ahí sería invalidar y repintar sin parar. */
  progreso: Record<string, Progreso>;
  /** Por dónde va la preparación, por id de DESCARGA. Aparte del anterior y no en la misma
   *  clave: mientras se convierte, el original sigue sembrando en aMule, así que los dos
   *  progresos existen a la vez. Compartiendo el hash, cada evento de aMule borraba el
   *  porcentaje de la conversión y al revés, y la barra saltaba entre dos cosas distintas.
   *
   *  Guarda la fase entera y no solo el porcentaje: analizar y leer los subtítulos por OCR
   *  pasan ANTES de ffmpeg y no tienen porcentaje que enseñar, pero sí tienen algo que
   *  contar. */
  progresoConversion: Record<string, ProgresoConversion>;
  /** Se incrementa cuando cambia algo que SÍ obliga a recargar la lista. */
  revision: number;
  /** Las líneas de conversión que llegan mientras alguien mira un registro, por descarga.
   *  Igual que el progreso: cambia varias veces por segundo y no puede pasar por la caché
   *  de consultas. */
  registros: Record<string, RegistroVivo>;
  /** Cuántos componentes están mirando el registro de cada descarga. Sin nadie mirando NO
   *  se guarda nada: ffmpeg escribe miles de líneas por conversión y no tiene sentido
   *  acumularlas por si acaso. */
  observados: Record<string, number>;
  /** Se incrementa cuando un intento empieza o termina, para que quien lo tenga abierto
   *  vuelva a pedirlo: al cerrarse, la versión buena es ya la de la base de datos. */
  revisionRegistro: number;
  setConectado: (v: boolean) => void;
  aplicarProgreso: (hash: string, p: Progreso) => void;
  aplicarProgresoConversion: (downloadId: string, progreso: ProgresoConversion) => void;
  olvidarProgresoConversion: (downloadId: string) => void;
  observarRegistro: (downloadId: string) => void;
  dejarDeObservarRegistro: (downloadId: string) => void;
  anadirLineas: (downloadId: string, runId: string, lineas: LineaRegistro[]) => void;
  bumpRegistro: () => void;
  bump: () => void;
}

/** Copiada de `api.ts` a propósito, no importada: una constante importada no se puede
 *  plegar, y sin plegarla el módulo de la demo acaba en el paquete de producción. */
const modoDemo = import.meta.env.VITE_DEMO === '1';

export const useEventos = create<EstadoEventos>((set) => ({
  conectado: false,
  progreso: {},
  progresoConversion: {},
  revision: 0,
  registros: {},
  observados: {},
  revisionRegistro: 0,
  setConectado: (conectado) => set({ conectado }),
  aplicarProgreso: (hash, p) => set((s) => ({ progreso: { ...s.progreso, [hash]: p } })),
  aplicarProgresoConversion: (id, progreso) =>
    set((s) => ({ progresoConversion: { ...s.progresoConversion, [id]: progreso } })),
  // Al acabar un intento se suelta su progreso: si se quedara, el siguiente empezaría
  // pintando el 98 % del anterior hasta que ffmpeg dijera la primera cifra.
  olvidarProgresoConversion: (id) =>
    set((s) => {
      if (!(id in s.progresoConversion)) return {};
      const progresoConversion = { ...s.progresoConversion };
      delete progresoConversion[id];
      return { progresoConversion };
    }),
  observarRegistro: (id) =>
    set((s) => ({ observados: { ...s.observados, [id]: (s.observados[id] ?? 0) + 1 } })),
  dejarDeObservarRegistro: (id) =>
    set((s) => {
      const quedan = (s.observados[id] ?? 1) - 1;
      if (quedan > 0) return { observados: { ...s.observados, [id]: quedan } };
      // Al cerrar la última ventana se suelta lo acumulado: al volver a abrirla se pide
      // otra vez, y para entonces lo de antes ya está en la base de datos.
      const observados = { ...s.observados };
      const registros = { ...s.registros };
      delete observados[id];
      delete registros[id];
      return { observados, registros };
    }),
  anadirLineas: (id, runId, lineas) =>
    set((s) => {
      if (!s.observados[id]) return {};
      const previo = s.registros[id];
      const acumuladas = previo && previo.runId === runId ? [...previo.lineas, ...lineas] : lineas;
      return {
        registros: {
          ...s.registros,
          [id]: { runId, lineas: acumuladas.slice(-MAX_LINEAS_VIVAS) },
        },
      };
    }),
  bumpRegistro: () => set((s) => ({ revisionRegistro: s.revisionRegistro + 1 })),
  bump: () => set((s) => ({ revision: s.revision + 1 })),
}));

/** Abre el stream de la API. `EventSource` reconecta solo con retroceso; solo se avisa
 *  al usuario cuando la conexión está realmente cerrada. */
export function conectarEventos(onRecargar: () => void): () => void {
  // En modo demo no hay servidor que emita nada: el latido lo pone `demo.ts`. Va por aquí
  // y no por una pantalla aparte porque lo que hay que poder revisar sin backend es
  // precisamente lo que se mueve — la barra, la velocidad, el cambio de estado.
  if (modoDemo) {
    const { setConectado, aplicarProgreso, bump } = useEventos.getState();
    setConectado(true);
    // Diferido, igual que en `api.ts`: así el módulo de la demo no entra en el paquete de
    // producción. Como llega después, hay que contemplar que alguien desmonte mientras
    // tanto — si no, el latido seguiría corriendo sin nadie escuchándolo.
    let cerrar: (() => void) | null = null;
    let desmontado = false;
    void import('./demo.js').then(({ conectarDemo }) => {
      if (desmontado) return;
      cerrar = conectarDemo((e) => {
        if (e.tipo === 'progreso') return aplicarProgreso(e.hash, e.d);
        bump();
        onRecargar();
      });
    });
    return () => {
      desmontado = true;
      cerrar?.();
      setConectado(false);
    };
  }

  const es = new EventSource('/api/events', { withCredentials: true });
  const {
    setConectado, aplicarProgreso, aplicarProgresoConversion, olvidarProgresoConversion,
    anadirLineas, bumpRegistro, bump,
  } = useEventos.getState();

  es.addEventListener('open', () => setConectado(true));

  es.addEventListener('download:progress', (ev) => {
    const d = JSON.parse((ev as MessageEvent<string>).data) as { hash: string } & Progreso;
    aplicarProgreso(d.hash, d);
  });

  // Por dónde va la preparación: analizando, leyendo subtítulos o bajo ffmpeg. Como el de
  // la descarga: por memoria y sin recargar nada, porque llega cada pocos segundos durante
  // horas.
  es.addEventListener('transcode:progress', (ev) => {
    const d = JSON.parse((ev as MessageEvent<string>).data) as {
      downloadId: string;
      progreso: ProgresoConversion;
    };
    aplicarProgresoConversion(d.downloadId, d.progreso);
  });

  // El registro de una conversión NO recarga nada: llega a ráfagas mientras ffmpeg trabaja
  // y solo lo guarda quien tenga esa ventana abierta. Meterlo en la lista de abajo sería
  // repintar Descargas entera varias veces por segundo durante dos horas.
  es.addEventListener('transcode:log', (ev) => {
    const d = JSON.parse((ev as MessageEvent<string>).data) as {
      downloadId: string;
      runId: string;
      lineas: LineaRegistro[];
    };
    anadirLineas(d.downloadId, d.runId, d.lineas);
  });

  // Que un intento empiece o acabe sí es un cambio: cierra el «en curso» del encabezado y
  // deja la versión definitiva en la base de datos.
  es.addEventListener('transcode:run', (ev) => {
    const d = JSON.parse((ev as MessageEvent<string>).data) as {
      downloadId: string;
      estado: string;
    };
    if (d.estado !== 'en_curso') olvidarProgresoConversion(d.downloadId);
    bumpRegistro();
  });

  for (const tipo of [
    'download:status', 'download:added', 'download:removed', 'library:synced',
    // La cola de conversión solo vive en la memoria de la API: sin este aviso no habría
    // forma de enterarse de que a una película le llegó el turno salvo preguntando.
    'transcode:queue',
  ]) {
    es.addEventListener(tipo, () => {
      bump();
      onRecargar();
    });
  }

  // Se marca como caída en cuanto hay error, no solo con el stream cerrado del todo.
  // `EventSource` reintenta indefinidamente y mientras tanto se queda en `CONNECTING`, así
  // que mirando solo `CLOSED` una API reiniciada dejaba la barra diciendo «Conectado» con
  // los avisos sin llegar. `open` la devuelve a verde al reconectar.
  es.addEventListener('error', () => setConectado(false));

  return () => {
    es.close();
    setConectado(false);
  };
}

export function formatearTamano(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 2)} ${u[i]}`;
}

export function formatearVelocidad(bps: number): string {
  return bps > 0 ? `${formatearTamano(bps)}/s` : '—';
}

/** Nombre de un códec de subtítulo de imagen tal como se dice fuera de ffmpeg.
 *  `hdmv_pgs_subtitle` no se le enseña a nadie. */
function nombreDeSubtitulo(codec: string): string {
  if (codec === 'hdmv_pgs_subtitle') return 'PGS';
  if (codec === 'dvd_subtitle') return 'VobSub';
  return codec;
}

/** Lo que se pinta de una conversión en curso, en un solo sitio para las dos pantallas.
 *
 *  `transcoding` son tres cosas seguidas —analizar, leer los subtítulos de imagen por OCR y
 *  ffmpeg—, y antes todas se pintaban igual: barra indefinida y «Convirtiendo a MP4…», así
 *  que un OCR de tres subtítulos eran minutos sin una sola señal de que algo avanzara.
 *
 *  Durante el OCR el porcentaje es **imágenes leídas**, no una estimación (D-19): el
 *  subtítulo se lee por bloques y cada bloque que termina son las suyas. Donde no se puede
 *  contar —mientras se extrae la pista, o con un lector sin bloques— no se inventa ninguno
 *  y queda el reloj, que al menos dice que sigue vivo. */
export function describirConversion(p: ProgresoConversion | undefined): {
  /** Para el chip de estado: corto y sin cifras. */
  etiqueta: string;
  /** Para el pie de la barra: lo que está pasando, con su detalle. */
  texto: string;
  nota: string;
  /** `null` deja la barra indefinida, que es lo honesto cuando no hay nada que medir. */
  porcentaje: number | null;
} {
  // Sin ningún evento todavía —la pantalla acaba de abrirse— se dice lo de siempre: es lo
  // que la fila ya decía, y en un segundo llega la fase de verdad.
  if (!p) {
    return {
      etiqueta: 'Convirtiendo',
      texto: 'Convirtiendo a MP4…',
      nota: 'Preparando la película. En cuanto la API diga por dónde va, esto se concreta.',
      porcentaje: null,
    };
  }

  if (p.fase === 'analizando') {
    return {
      etiqueta: 'Analizando',
      texto: 'Analizando el original…',
      nota: 'Mirando con ffprobe qué pistas trae el fichero, antes de decidir el plan. Son segundos.',
      porcentaje: null,
    };
  }

  if (p.fase === 'ocr' && p.ocr) {
    const o = p.ocr;
    const cual = `${o.numero} de ${o.total}`;
    const pista = `${nombreDeSubtitulo(o.codec)}${o.idioma ? `, ${o.idioma}` : ''}`;
    const que = o.etapa === 'extrayendo' ? 'extrayendo la pista' : 'leyendo con tesseract';
    // Cuántas imágenes van, y a este ritmo cuánto queda de ESTE subtítulo. El ritmo sale de
    // lo ya leído, así que no hay nada que estimar: si va lento, se ve que va lento.
    const contadas = o.imagenes ? ` · ${o.leidas} de ${o.imagenes} imágenes` : '';
    const falta =
      o.imagenes && o.leidas > 0 && o.segundos > 0
        ? ` · quedan ~${formatearDuracion((o.segundos / o.leidas) * (o.imagenes - o.leidas))}`
        : ` · ${formatearDuracion(o.segundos)}`;

    return {
      etiqueta: 'Leyendo subtítulos',
      texto: `Leyendo subtítulos · ${cual} · ${pista} · ${que}${contadas}${falta}`,
      nota:
        'MP4 no admite subtítulos de imagen, así que hay que pasarlos a texto antes de ' +
        'convertir. Se lee por bloques, y el porcentaje son las imágenes ya leídas: es ' +
        'trabajo hecho, no una estimación. Puede tardar más que la propia conversión.',
      porcentaje: p.porcentaje,
    };
  }

  return {
    etiqueta: 'Convirtiendo',
    texto:
      p.porcentaje == null ? 'Convirtiendo a MP4…' : `Convirtiendo a MP4 · ${p.porcentaje.toFixed(1)}%`,
    nota:
      'Pasando a MP4 con ffmpeg. Si ya venía en H.264 y AC3 son segundos; si hay que ' +
      'recodificar, puede tardar horas.',
    porcentaje: p.porcentaje,
  };
}

/** Las carátulas de HispaShare vienen como ruta relativa (`images/titles/78/cover.jpg`). */
export function urlPoster(ruta: string | null | undefined): string | null {
  if (!ruta) return null;
  return ruta.startsWith('http') ? ruta : `https://www.hispashare.org/${ruta}`;
}

/** Segundos que faltan al ritmo de ESTE instante, o `null` si no avanza.
 *
 *  Es nuestra cuenta, no la de aMule: la suya solo viaja con el detalle —cientos de partes
 *  de JSON— y no se puede pedir cada segundo por descarga solo para pintar un número. En
 *  eD2k la velocidad da saltos enormes, así que esto es una estimación y la interfaz lo
 *  dice con un `~`. A 0 B/s no se estima nada: dividir por cero daría un infinito, y
 *  redondearlo a «99 días» sería inventarse un dato. */
export function estimarEta(bytesRestantes: number, bps: number): number | null {
  if (bps <= 0 || bytesRestantes <= 0) return null;
  return bytesRestantes / bps;
}

export function formatearDuracion(segundos: number | null | undefined): string {
  if (segundos == null || !Number.isFinite(segundos) || segundos < 0) return '—';
  if (segundos < 60) return `${Math.round(segundos)} s`;
  const min = Math.floor(segundos / 60) % 60;
  if (segundos < 3600) return `${min} min`;
  const horas = Math.floor(segundos / 3600) % 24;
  if (segundos < 86_400) return `${horas} h ${min} min`;
  const dias = Math.floor(segundos / 86_400);
  // Por encima de un mes el número deja de significar nada: media hora de buena velocidad
  // lo cambia entero. Se dice el orden de magnitud y ya.
  return dias > 30 ? 'más de un mes' : `${dias} d ${horas} h`;
}

/** Un unix ts en segundos, contado desde ahora. */
export function formatearHace(unixTs: number | null | undefined): string {
  if (unixTs == null) return 'nunca';
  return `hace ${formatearDuracion(Date.now() / 1000 - unixTs)}`;
}
